<script setup>
import { computed, onBeforeUnmount, reactive, watch } from 'vue'
import { X, Sprout, Droplets, Layers, Flower2, Sun, Bird, Egg, Snowflake, Sparkles } from '@lucide/vue'

const props = defineProps({
  open: { type: Boolean, default: false },
  collections: { type: Array, default: () => [] },
  badges: { type: Array, default: () => [] },
})
const emit = defineEmits(['close'])

/* ———— 图片素材约定（由主控提供，置于 public/badges/）————
   稀有度底座：/badges/medallion-copper.png
              /badges/medallion-silver.png
              /badges/medallion-gold.png
              /badges/medallion-legend.png
   加载失败时移除 <img>，退化为纯 CSS + lucide 底座，展示不受影响。 */
const brokenMedallions = reactive({})
const medallionSrc = rarity => `/badges/medallion-${rarity || 'copper'}.png`
const onMedallionError = rarity => { brokenMedallions[rarity] = true }

const rarityLabels = { copper: '铜章', silver: '银章', gold: '金章', legend: '传说' }

/* 系列图标：code 别名精确匹配 → 名称关键词 → 兜底 Sparkles */
const iconByCode = {
  seed: Sprout, seeds: Sprout,
  dew: Droplets, streak: Droplets,
  soil: Layers, reviews: Layers,
  pollen: Flower2, pollination: Flower2, mastery: Flower2,
  gardener: Sun, habit: Sun,
  hummingbird: Bird, precision: Bird,
  hidden: Egg, easter: Egg, secret: Egg,
  seasonal: Snowflake, season: Snowflake,
}
const iconByName = [
  ['种子', Sprout], ['晨露', Droplets], ['连续', Droplets],
  ['土壤', Layers], ['复习', Layers],
  ['授粉', Flower2], ['掌握', Flower2],
  ['园丁', Sun], ['习惯', Sun],
  ['蜂鸟', Bird], ['精准', Bird],
  ['彩蛋', Egg],
  ['四季', Snowflake], ['限定', Snowflake],
]
function resolveIcon(collection) {
  if (!collection) return Sparkles
  const code = String(collection.code || '').toLowerCase()
  if (iconByCode[code]) return iconByCode[code]
  const name = String(collection.name || '')
  for (const [keyword, icon] of iconByName) if (name.includes(keyword)) return icon
  return Sparkles
}
const iconForBadge = badge => resolveIcon(props.collections.find(c => c.code === badge.collection))

const totalCount = computed(() => props.badges.length)
const unlockedCount = computed(() => props.badges.filter(b => b.unlocked).length)
const totalDeg = computed(() => (totalCount.value ? Math.round((unlockedCount.value / totalCount.value) * 360) : 0))

/* 即将解锁：未解锁 + 非彩蛋，按 progress/target 从高到低取前 3 */
const upcoming = computed(() => props.badges
  .filter(b => !b.unlocked && !b.hidden && b.target > 0)
  .map(b => ({ ...b, ratio: Math.min(1, (b.progress || 0) / b.target) }))
  .sort((a, b) => b.ratio - a.ratio)
  .slice(0, 3))

/* 系列分组：保持 collections 顺序；未匹配 collection 的徽章归入「其他」 */
const groups = computed(() => {
  const map = new Map()
  props.collections.forEach(c => map.set(c.code, { ...c, icon: resolveIcon(c), items: [] }))
  const loose = []
  props.badges.forEach(b => {
    const group = map.get(b.collection)
    if (group) group.items.push(b)
    else loose.push(b)
  })
  const list = [...map.values()].filter(g => g.items.length)
  if (loose.length) {
    list.push({ code: '_other', name: '其他', total: loose.length, unlocked_count: loose.filter(b => b.unlocked).length, icon: Sparkles, items: loose })
  }
  return list
})

const pct = b => Math.round(Math.min(1, (b.progress || 0) / (b.target || 1)) * 100)

/* 季节限定时间窗：'2026-03-20' → '3.20' */
function fmtWindow(w) {
  if (!w || !w.start || !w.end) return ''
  const fmt = value => {
    const iso = String(value).match(/^\d{4}-(\d{1,2})-(\d{1,2})/)
    if (iso) return `${Number(iso[1])}.${Number(iso[2])}`
    const loose = String(value).match(/(\d{1,2})[-/.](\d{1,2})/)
    return loose ? `${Number(loose[1])}.${Number(loose[2])}` : String(value)
  }
  return `${fmt(w.start)} – ${fmt(w.end)} 限定`
}

/* 打开时锁定背景滚动 + Esc 关闭 */
const onKeydown = event => { if (event.key === 'Escape') emit('close') }
watch(() => props.open, v => {
  document.body.style.overflow = v ? 'hidden' : ''
  if (v) window.addEventListener('keydown', onKeydown)
  else window.removeEventListener('keydown', onKeydown)
}, { immediate: true })
onBeforeUnmount(() => {
  document.body.style.overflow = ''
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="album">
      <div v-if="open" class="album-overlay" @click.self="emit('close')">
        <section class="album-panel" role="dialog" aria-modal="true" aria-label="徽章图鉴">
          <header class="album-head">
            <div>
              <p class="eyebrow">BADGE ALBUM</p>
              <h2>徽章图鉴</h2>
            </div>
            <button class="album-close" type="button" aria-label="关闭图鉴" @click="emit('close')"><X :size="20" /></button>
          </header>

          <div class="album-top">
            <div class="album-ring" :style="{ '--deg': totalDeg + 'deg' }">
              <span><b>{{ unlockedCount }}</b><small>/ {{ totalCount }}</small></span>
            </div>
            <div class="album-chips">
              <span v-for="c in collections" :key="c.code" class="album-chip" :class="{ done: c.total > 0 && c.unlocked_count >= c.total }">
                {{ c.name }} {{ c.unlocked_count }}/{{ c.total }}
              </span>
            </div>
          </div>

          <section v-if="upcoming.length" class="album-soon">
            <h3><Sparkles />即将解锁</h3>
            <ul>
              <li v-for="b in upcoming" :key="b.code" :class="[`rarity-${b.rarity}`, 'locked']">
                <span class="badge-medallion sm">
                  <img v-if="!brokenMedallions[b.rarity]" :src="medallionSrc(b.rarity)" alt="" @error="onMedallionError(b.rarity)" />
                  <component :is="iconForBadge(b)" />
                </span>
                <span class="badge-info">
                  <b>{{ b.name }}</b>
                  <span class="badge-progress"><span class="bar"><i :style="{ width: pct(b) + '%' }"></i></span><em>{{ Math.min(b.progress || 0, b.target) }}/{{ b.target }}</em></span>
                </span>
              </li>
            </ul>
          </section>

          <section v-for="g in groups" :key="g.code" class="album-group">
            <h3><component :is="g.icon" />{{ g.name }}<small>{{ g.unlocked_count }}/{{ g.total }}</small></h3>
            <ul class="badge-grid">
              <li v-for="b in g.items" :key="b.code" class="badge-tile" :class="[`rarity-${b.rarity}`, { locked: !b.unlocked, secret: b.hidden && !b.unlocked }]">
                <span class="badge-medallion">
                  <img v-if="!brokenMedallions[b.rarity]" :src="medallionSrc(b.rarity)" alt="" @error="onMedallionError(b.rarity)" />
                  <component :is="b.hidden && !b.unlocked ? Egg : g.icon" />
                </span>
                <span class="badge-info">
                  <b>
                    {{ b.hidden && !b.unlocked ? '???' : b.name }}
                    <i v-if="b.seasonal && b.window" class="badge-window">{{ fmtWindow(b.window) }}</i>
                  </b>
                  <small v-if="b.unlocked">{{ (b.unlocked_at || '').slice(0, 10) }} 解锁 · {{ rarityLabels[b.rarity] || b.rarity }}</small>
                  <small v-else>{{ b.desc }}</small>
                  <span v-if="!b.unlocked && !b.hidden && b.target > 0" class="badge-progress">
                    <span class="bar"><i :style="{ width: pct(b) + '%' }"></i></span><em>{{ Math.min(b.progress || 0, b.target) }}/{{ b.target }}</em>
                  </span>
                </span>
              </li>
            </ul>
          </section>
          <p v-if="!groups.length" class="album-empty">徽章目录加载中…</p>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* —— 全屏弹层：重玻璃，遮罩可点关闭 —— */
.album-overlay { position: fixed; inset: 0; z-index: 120; display: grid; place-items: center; padding: 18px; background: rgba(245, 240, 230, .62); -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px); }
[data-theme="dark"] .album-overlay { background: rgba(22, 27, 23, .68); }
.album-panel { width: min(680px, 100%); max-height: min(84vh, 880px); overflow-y: auto; padding: 22px 20px 28px; border-radius: var(--radius-xl); background: var(--glass-bg-strong); -webkit-backdrop-filter: blur(var(--glass-blur-strong)) saturate(160%); backdrop-filter: blur(var(--glass-blur-strong)) saturate(160%); border: 1px solid var(--glass-border); box-shadow: var(--glass-highlight), var(--glass-shadow); }
@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .album-panel { background: var(--glass-fallback); -webkit-backdrop-filter: none; backdrop-filter: none; }
}

.album-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 18px; }
.album-head h2 { font: 700 22px/1.2 var(--font-display); }
.album-close { flex: none; display: grid; place-items: center; width: 44px; height: 44px; padding: 0; border: 1px solid var(--glass-border); border-radius: 50%; background: var(--glass-bg-inset); color: var(--muted); }
.album-close:hover { color: var(--ink); }

/* —— 顶部：总完成度环 + 系列完成度 chips —— */
.album-top { display: flex; align-items: center; gap: 18px; margin-bottom: 20px; }
.album-ring { --deg: 0deg; position: relative; flex: none; width: 92px; height: 92px; border-radius: 50%; display: grid; place-items: center; background: conic-gradient(var(--gold) var(--deg), var(--sage-soft) 0); box-shadow: 0 8px 22px var(--sage-glow); }
.album-ring::before { content: ""; position: absolute; inset: 8px; border-radius: 50%; background: var(--glass-fallback); }
.album-ring span { position: relative; display: flex; align-items: baseline; gap: 2px; }
.album-ring b { font: 700 24px var(--font-display); }
.album-ring small { color: var(--muted); font-size: 11px; }
.album-chips { display: flex; flex-wrap: wrap; gap: 7px; }
.album-chip { padding: 5px 11px; border-radius: var(--radius-full); border: 1px solid var(--glass-border); background: var(--glass-bg-inset); color: var(--muted); font-size: 12px; }
.album-chip.done { border-color: var(--sage); background: var(--sage-soft); color: var(--sage-dark); font-weight: 700; }

/* —— 即将解锁（目标梯度最强位置顶） —— */
.album-soon { margin-bottom: 22px; padding: 14px; border-radius: var(--radius-lg); background: var(--glass-bg-inset); border: 1px solid var(--glass-border); }
.album-soon h3, .album-group h3 { display: flex; align-items: center; gap: 8px; font: 700 14px var(--font); color: var(--sage-dark); margin: 0 0 12px; }
.album-soon h3 svg, .album-group h3 svg { width: 17px; height: 17px; }
.album-group h3 small { margin-left: auto; color: var(--muted); font-weight: 600; font-size: 12px; }
.album-soon ul { display: grid; gap: 10px; list-style: none; margin: 0; padding: 0; }
.album-soon li { display: flex; align-items: center; gap: 12px; }

/* —— 系列分组 + 徽章砖 —— */
.album-group { margin-bottom: 22px; }
.badge-grid { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
@media (max-width: 560px) { .badge-grid { grid-template-columns: 1fr; } }
.badge-tile { display: flex; align-items: center; gap: 12px; padding: 12px; border-radius: var(--radius-md); background: var(--glass-bg-inset); border: 1px solid var(--glass-border); }
.badge-tile.locked { opacity: .82; }
.badge-tile.rarity-legend:not(.locked) { border-color: color-mix(in srgb, var(--gold) 45%, transparent); }

/* 底座：CSS 圆盘 + medallion 图片叠底（存在时） */
.badge-medallion { position: relative; flex: none; width: 52px; height: 52px; border-radius: 50%; display: grid; place-items: center; border: 1px solid var(--glass-border); background: var(--glass-bg-inset); color: var(--muted); }
.badge-medallion.sm { width: 40px; height: 40px; }
.badge-medallion img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; }
.badge-medallion svg { position: relative; width: 24px; height: 24px; }
.badge-medallion.sm svg { width: 19px; height: 19px; }

/* 稀有度配色：已解锁绽放态（.locked 覆盖为灰色种子剪影，须置后） */
.rarity-copper .badge-medallion { background: color-mix(in srgb, var(--terracotta) 22%, transparent); border-color: transparent; color: var(--terracotta); }
.rarity-silver .badge-medallion { background: color-mix(in srgb, var(--muted) 20%, transparent); border-color: transparent; color: var(--muted); }
.rarity-gold .badge-medallion { background: color-mix(in srgb, var(--gold) 24%, transparent); border-color: transparent; color: var(--gold); }
.rarity-legend .badge-medallion { background: color-mix(in srgb, var(--gold) 26%, transparent); border-color: transparent; color: var(--gold); box-shadow: 0 0 0 2px var(--sage-glow), 0 8px 22px var(--sage-glow); }
.locked .badge-medallion { background: var(--glass-bg-inset); border: 1.5px dashed var(--glass-border); color: var(--muted-2); box-shadow: none; }

.badge-info { display: grid; gap: 3px; min-width: 0; flex: 1; }
.badge-info > b { font-size: 13.5px; line-height: 1.3; display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
.badge-info > small { color: var(--muted); font-size: 12px; line-height: 1.45; }
.secret .badge-info > small { font-style: italic; }
.badge-window { font-style: normal; padding: 2px 8px; border-radius: var(--radius-full); background: color-mix(in srgb, var(--gold) 18%, transparent); color: var(--gold); font-size: 10px; font-weight: 700; white-space: nowrap; }

.badge-progress { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
.badge-progress .bar { flex: 1; height: 6px; border-radius: 99px; background: var(--sage-soft); overflow: hidden; }
.badge-progress .bar i { display: block; height: 100%; border-radius: inherit; background: var(--sage); }
.badge-progress em { font-style: normal; font-size: 11px; color: var(--muted); white-space: nowrap; }

.album-empty { padding: 30px; text-align: center; color: var(--muted); }

/* —— 进出动效 —— */
.album-enter-active, .album-leave-active { transition: opacity .26s ease; }
.album-enter-from, .album-leave-to { opacity: 0; }
.album-enter-active .album-panel, .album-leave-active .album-panel { transition: transform .26s ease; }
.album-enter-from .album-panel, .album-leave-to .album-panel { transform: translateY(14px) scale(.985); }

@media (prefers-reduced-motion: reduce) {
  .album-enter-active, .album-leave-active, .album-enter-active .album-panel, .album-leave-active .album-panel { transition: none; }
}
</style>
